import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Package, ShoppingBag, LogOut, X, Image as ImageIcon, FileText, Sparkles, Users, DollarSign, Settings, Link2, Copy, Key, Download, Upload, Tags, Contact } from "lucide-react";
import { api, formatARS, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { SELLERS } from "@/lib/sellers";
import PricesTab from "./admin/PricesTab";
import CustomersTab from "./admin/CustomersTab";
import ProductPriceHistory from "./admin/ProductPriceHistory";
import TopProducts from "./admin/TopProducts";

const EMPTY = { name: "", description: "", price: 0, category: "Limpieza", stock: 0, image: "", featured: false };

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [importingJson, setImportingJson] = useState(false);
  const [commissionRate, setCommissionRate] = useState(() => {
    const v = parseFloat(localStorage.getItem("produlimp_commission_pct"));
    return Number.isFinite(v) ? v : 5;
  });

  const loadProducts = () => api.get("/products").then((r) => setProducts(r.data));
  const loadOrders = () => api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
  const loadStats = () => api.get("/stats/sellers").then((r) => setStats(r.data)).catch(() => {});

  const exportProducts = async () => {
    try {
      const { data } = await api.get("/backup/products");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const date = new Date().toISOString().slice(0, 10);
      a.download = `produlimp-productos-${date}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Backup descargado: ${data.count} productos`);
    } catch {
      toast.error("Error al exportar productos");
    }
  };

  useEffect(() => { if (user?.role === "admin") { loadProducts(); loadOrders(); loadStats(); } }, [user]);
  useEffect(() => { localStorage.setItem("produlimp_commission_pct", String(commissionRate)); }, [commissionRate]);

  if (loading) return <div className="p-20 text-center">Cargando...</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Producto eliminado");
    loadProducts();
  };

  const removeOrder = async (id) => {
    if (!window.confirm("¿Eliminar este pedido?")) return;
    await api.delete(`/orders/${id}`);
    loadOrders();
    loadStats();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-dashboard">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-600">Panel Admin</span>
          <h1 className="font-heading font-extrabold text-3xl md:text-4xl tracking-tight mt-1">Hola, {user.name}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setShowChangePw(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-blue-700 border-2 border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition-colors" data-testid="change-password-btn">
            <Key className="w-4 h-4" /> Cambiar contraseña
          </button>
          <button onClick={logout} className="btn-outline text-red-500 border-red-200 hover:border-red-500 hover:text-red-600" data-testid="admin-logout">
            <LogOut className="w-4 h-4" /> Cerrar sesión
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-6 border-b border-slate-200 overflow-x-auto">
        <button onClick={() => setTab("products")} className={`px-4 py-3 font-medium flex items-center gap-2 border-b-2 whitespace-nowrap ${tab === "products" ? "border-blue-600 text-blue-700" : "border-transparent text-slate-600"}`} data-testid="tab-products">
          <Package className="w-4 h-4" /> Productos ({products.length})
        </button>
        <button onClick={() => setTab("orders")} className={`px-4 py-3 font-medium flex items-center gap-2 border-b-2 whitespace-nowrap ${tab === "orders" ? "border-blue-600 text-blue-700" : "border-transparent text-slate-600"}`} data-testid="tab-orders">
          <ShoppingBag className="w-4 h-4" /> Pedidos ({orders.length})
        </button>
        <button onClick={() => setTab("commissions")} className={`px-4 py-3 font-medium flex items-center gap-2 border-b-2 whitespace-nowrap ${tab === "commissions" ? "border-blue-600 text-blue-700" : "border-transparent text-slate-600"}`} data-testid="tab-commissions">
          <Users className="w-4 h-4" /> Comisiones
        </button>
        <button onClick={() => setTab("prices")} className={`px-4 py-3 font-medium flex items-center gap-2 border-b-2 whitespace-nowrap ${tab === "prices" ? "border-blue-600 text-blue-700" : "border-transparent text-slate-600"}`} data-testid="tab-prices">
          <Tags className="w-4 h-4" /> Precios
        </button>
        <button onClick={() => setTab("customers")} className={`px-4 py-3 font-medium flex items-center gap-2 border-b-2 whitespace-nowrap ${tab === "customers" ? "border-blue-600 text-blue-700" : "border-transparent text-slate-600"}`} data-testid="tab-customers">
          <Contact className="w-4 h-4" /> Clientes
        </button>
      </div>

      {tab === "products" && (
        <>
          <div className="flex flex-wrap justify-end gap-2 mb-4">
            <button onClick={exportProducts} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-green-700 border-2 border-green-200 hover:bg-green-50 transition-colors" data-testid="export-products-btn">
              <Download className="w-4 h-4" /> Exportar (backup)
            </button>
            <button onClick={() => setImportingJson(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-blue-700 border-2 border-blue-200 hover:bg-blue-50 transition-colors" data-testid="import-json-btn">
              <Upload className="w-4 h-4" /> Restaurar backup
            </button>
            <button onClick={() => setImporting(true)} className="btn-secondary" data-testid="import-pdf-btn">
              <FileText className="w-4 h-4" /> Importar desde PDF
            </button>
            <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary" data-testid="new-product-btn">
              <Plus className="w-4 h-4" /> Nuevo producto
            </button>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-3">Producto</th>
                  <th className="text-left p-3 hidden md:table-cell">Categoría</th>
                  <th className="text-right p-3">Precio</th>
                  <th className="text-center p-3 hidden sm:table-cell">Stock</th>
                  <th className="text-right p-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-product-row-${p.id}`}>
                    <td className="p-3 flex items-center gap-3">
                      <img src={p.image} alt="" className="w-12 h-12 object-cover rounded-lg bg-slate-100 flex-shrink-0" onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1556228578-8c89e6adf883?q=80&w=200"; }} />
                      <div className="min-w-0">
                        <div className="font-medium truncate">{p.name}</div>
                        <div className="flex items-center gap-2">
                          {p.sku && <span className="text-[10px] font-mono text-blue-700 uppercase">{p.sku}</span>}
                          {p.featured && <span className="text-[10px] text-red-500 font-bold uppercase tracking-wider">Destacado</span>}
                        </div>
                      </div>
                    </td>
                    <td className="p-3 hidden md:table-cell"><span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium">{p.category}</span></td>
                    <td className="p-3 text-right font-semibold">{formatARS(p.price)}</td>
                    <td className="p-3 text-center hidden sm:table-cell">{p.stock}</td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => setEditing(p)} className="p-2 rounded-lg hover:bg-blue-50 text-blue-600" data-testid={`admin-edit-${p.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => remove(p.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500" data-testid={`admin-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {products.length === 0 && (
                  <tr><td colSpan="5" className="p-10 text-center text-slate-500">No hay productos aún.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "orders" && (
        <>
        <TopProducts />
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-3">Cliente</th>
                <th className="text-left p-3 hidden md:table-cell">Contacto</th>
                <th className="text-left p-3">Items</th>
                <th className="text-right p-3">Total</th>
                <th className="text-left p-3 hidden sm:table-cell">Fecha</th>
                <th className="text-right p-3"></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-slate-100 align-top" data-testid={`admin-order-${o.id}`}>
                  <td className="p-3 font-medium">
                    {o.customer_name}
                    <div className="text-xs text-slate-500">{o.customer_email}</div>
                    <div className="text-xs text-slate-500">{o.customer_address}{o.customer_city && `, ${o.customer_city}`}</div>
                    {o.customer_cuit && <div className="text-xs text-slate-500 font-mono">CUIT: {o.customer_cuit}</div>}
                  </td>
                  <td className="p-3 hidden md:table-cell">{o.customer_phone || "—"}{o.seller_name && <div className="text-xs text-blue-600 font-medium mt-1">Vend. {o.seller_code} - {o.seller_name}</div>}</td>
                  <td className="p-3 text-xs">
                    {o.items.map((it, i) => <div key={i}>{it.code ? <span className="font-mono text-blue-700">{it.code}</span> : null} {it.quantity}× {it.name}</div>)}
                  </td>
                  <td className="p-3 text-right font-semibold">{formatARS(o.total)}</td>
                  <td className="p-3 text-xs hidden sm:table-cell">{new Date(o.created_at).toLocaleString("es-AR")}</td>
                  <td className="p-3 text-right">
                    <button onClick={() => removeOrder(o.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr><td colSpan="6" className="p-10 text-center text-slate-500">Sin pedidos todavía.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        </>
      )}

      {tab === "commissions" && <CommissionsView stats={stats} commissionRate={commissionRate} setCommissionRate={setCommissionRate} />}

      {tab === "prices" && <PricesTab onApplied={loadProducts} />}

      {tab === "customers" && <CustomersTab />}

      {editing && <ProductModal product={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); loadProducts(); }} />}
      {importing && <PdfImportModal onClose={() => setImporting(false)} onImported={() => { setImporting(false); loadProducts(); }} />}
      {importingJson && <JsonImportModal onClose={() => setImportingJson(false)} onImported={() => { setImportingJson(false); loadProducts(); }} />}
      {showChangePw && <ChangePasswordModal onClose={() => setShowChangePw(false)} />}
    </div>
  );
}

function PdfImportModal({ onClose, onImported }) {
  const [stage, setStage] = useState("upload"); // upload | preview | saving
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState({});
  const [progress, setProgress] = useState("");

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("El archivo debe ser un PDF"); return; }
    setProgress("Analizando PDF con IA, esto puede tardar 30-60 segundos...");
    setStage("loading");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      if (!data.products?.length) {
        toast.error("No se encontraron productos en el PDF");
        setStage("upload");
        return;
      }
      setProducts(data.products);
      const sel = {};
      data.products.forEach((_, i) => { sel[i] = true; });
      setSelected(sel);
      setStage("preview");
      toast.success(`Se detectaron ${data.products.length} productos`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al procesar el PDF");
      setStage("upload");
    } finally {
      e.target.value = "";
    }
  };

  const update = (i, patch) => setProducts((arr) => arr.map((p, idx) => idx === i ? { ...p, ...patch } : p));

  const save = async () => {
    const toCreate = products.filter((_, i) => selected[i]);
    if (!toCreate.length) { toast.error("Seleccioná al menos un producto"); return; }
    setStage("saving");
    try {
      const { data } = await api.post("/products/bulk", { products: toCreate });
      toast.success(`${data.created} productos creados`);
      onImported();
    } catch {
      toast.error("Error al guardar los productos");
      setStage("preview");
    }
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="pdf-import-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-600" />
            <h2 className="font-heading font-bold text-xl">Importar productos desde PDF</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>

        {stage === "upload" && (
          <div className="p-8">
            <label className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-slate-300 rounded-2xl py-14 cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors" data-testid="pdf-dropzone">
              <FileText className="w-12 h-12 text-blue-500" />
              <span className="text-base font-semibold text-slate-700">Subí tu catálogo PDF</span>
              <span className="text-sm text-slate-500 max-w-md text-center">Nuestra IA analizará el documento y extraerá automáticamente los productos con sus precios y descripciones.</span>
              <span className="text-xs text-slate-400">PDF, hasta 30 MB</span>
              <input type="file" accept="application/pdf" className="hidden" onChange={onFile} data-testid="pdf-file-input" />
            </label>
            <ul className="text-xs text-slate-500 mt-4 space-y-1 list-disc pl-5">
              <li>Lo extraído se previsualiza antes de guardarse</li>
              <li>Vas a poder editar cada producto antes de crearlo</li>
              <li>Las imágenes se cargan después manualmente</li>
            </ul>
          </div>
        )}

        {stage === "loading" && (
          <div className="p-12 text-center">
            <div className="inline-block w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
            <p className="mt-4 text-slate-600">{progress}</p>
          </div>
        )}

        {stage === "preview" && (
          <>
            <div className="p-4 bg-blue-50 border-b border-blue-100 text-sm text-blue-900 flex items-center justify-between">
              <span>Se detectaron <strong>{products.length}</strong> productos. Editá lo que necesites y elegí cuáles importar.</span>
              <span className="font-semibold">{selectedCount} seleccionados</span>
            </div>
            <div className="overflow-auto flex-1">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b sticky top-0">
                  <tr>
                    <th className="p-2 w-10"><input type="checkbox" checked={selectedCount === products.length} onChange={(e) => { const s = {}; products.forEach((_, i) => s[i] = e.target.checked); setSelected(s); }} /></th>
                    <th className="text-left p-2">Producto</th>
                    <th className="text-left p-2 w-32">Categoría</th>
                    <th className="text-right p-2 w-28">Precio</th>
                    <th className="text-center p-2 w-20">Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p, i) => (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-2 text-center"><input type="checkbox" checked={!!selected[i]} onChange={(e) => setSelected({ ...selected, [i]: e.target.checked })} data-testid={`pdf-select-${i}`} /></td>
                      <td className="p-2">
                        <input value={p.name} onChange={(e) => update(i, { name: e.target.value })} className="w-full px-2 py-1 rounded border border-transparent hover:border-slate-200 focus:border-blue-400 outline-none font-medium" />
                        <input value={p.description} onChange={(e) => update(i, { description: e.target.value })} placeholder="Descripción..." className="w-full px-2 py-1 rounded border border-transparent hover:border-slate-200 focus:border-blue-400 outline-none text-xs text-slate-500 mt-1" />
                      </td>
                      <td className="p-2">
                        <select value={p.category} onChange={(e) => update(i, { category: e.target.value })} className="w-full px-2 py-1 rounded border border-slate-200 outline-none">
                          <option>Limpieza</option><option>Bazar</option><option>Perfumería</option><option>Ofertas</option>
                        </select>
                      </td>
                      <td className="p-2"><input type="number" value={p.price} onChange={(e) => update(i, { price: parseFloat(e.target.value) || 0 })} className="w-full px-2 py-1 rounded border border-slate-200 text-right outline-none" /></td>
                      <td className="p-2"><input type="number" value={p.stock} onChange={(e) => update(i, { stock: parseInt(e.target.value) || 0 })} className="w-full px-2 py-1 rounded border border-slate-200 text-center outline-none" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => { setStage("upload"); setProducts([]); }} className="px-4 py-2 rounded-lg hover:bg-slate-100">Volver</button>
              <button onClick={save} disabled={!selectedCount} className="btn-primary" data-testid="pdf-save-btn">
                Crear {selectedCount} producto{selectedCount === 1 ? "" : "s"}
              </button>
            </div>
          </>
        )}

        {stage === "saving" && (
          <div className="p-12 text-center">
            <div className="inline-block w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
            <p className="mt-4 text-slate-600">Guardando productos...</p>
          </div>
        )}
      </div>
    </div>
  );
}

function ProductModal({ product, onClose, onSaved }) {
  const isNew = !product.id;
  const [form, setForm] = useState({
    name: product.name || "",
    description: product.description || "",
    price: product.price || 0,
    category: product.category || "Limpieza",
    stock: product.stock || 0,
    image: product.image || "",
    sku: product.sku || "",
    featured: !!product.featured,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isNew) await api.post("/products", form);
      else await api.put(`/products/${product.id}`, form);
      toast.success(isNew ? "Producto creado" : "Producto actualizado");
      onSaved();
    } catch {
      toast.error("Error al guardar");
    } finally { setSaving(false); }
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("La imagen supera 8 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = `${BACKEND_URL}${data.url}`;
      setForm((f) => ({ ...f, image: fullUrl }));
      toast.success("Imagen subida correctamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir la imagen");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="product-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h2 className="font-heading font-bold text-xl">{isNew ? "Nuevo producto" : "Editar producto"}</h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <Field label="Nombre"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" data-testid="modal-name" /></Field>
          <Field label="Descripción"><textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input" data-testid="modal-description" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Precio (ARS)"><input type="number" min="0" step="0.001" required value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} className="input" data-testid="modal-price" /></Field>
            <Field label="Stock"><input type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: parseInt(e.target.value) || 0 })} className="input" data-testid="modal-stock" /></Field>
          </div>
          {!isNew && <ProductPriceHistory productId={product.id} />}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Categoría">
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input" data-testid="modal-category">
                <option>Limpieza</option><option>Bazar</option><option>Perfumería</option><option>Ofertas</option>
              </select>
            </Field>
            <Field label="Código (SKU)">
              <input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder="Ej: L001" className="input uppercase" data-testid="modal-sku" />
            </Field>
          </div>
          <Field label="Imagen del producto">
            <div className="space-y-2">
              {form.image ? (
                <div className="relative">
                  <img src={form.image} alt="preview" className="w-full h-48 object-contain rounded-lg border border-slate-200 bg-slate-50" onError={(e) => { e.currentTarget.style.opacity = "0.4"; }} />
                  <button type="button" onClick={() => setForm({ ...form, image: "" })} className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full" data-testid="modal-clear-image" aria-label="Quitar imagen">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-lg py-6 cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors" data-testid="modal-upload-dropzone">
                  <ImageIcon className="w-8 h-8 text-slate-400" />
                  <span className="text-sm text-slate-600">{uploading ? "Subiendo..." : "Hacé clic para subir una imagen"}</span>
                  <span className="text-xs text-slate-400">PNG, JPG, WEBP — máx 8 MB</span>
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={onFileChange} disabled={uploading} data-testid="modal-image-file" />
                </label>
              )}
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span className="flex-1 h-px bg-slate-200" /><span>o pegá una URL</span><span className="flex-1 h-px bg-slate-200" />
              </div>
              <input type="url" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="https://..." className="input" data-testid="modal-image" />
            </div>
          </Field>
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="modal-featured" />
            Marcar como destacado
          </label>
          <div className="flex justify-end gap-2 pt-3">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg hover:bg-slate-100">Cancelar</button>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="modal-save">{saving ? "Guardando..." : "Guardar"}</button>
          </div>
        </form>
        <style>{`.input{width:100%;padding:0.6rem 0.9rem;border:1px solid #e2e8f0;border-radius:0.5rem;outline:none;background:white}.input:focus{border-color:#60a5fa}`}</style>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><label className="text-sm font-medium text-slate-700 block mb-1">{label}</label>{children}</div>
);

function JsonImportModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [replace, setReplace] = useState(false);
  const [count, setCount] = useState(null);
  const [saving, setSaving] = useState(false);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".json")) { toast.error("El archivo debe ser .json"); e.target.value = ""; return; }
    try {
      const text = await f.text();
      const data = JSON.parse(text);
      const items = data.products || data;
      if (!Array.isArray(items)) throw new Error("invalid");
      setFile(f);
      setCount(items.length);
    } catch {
      toast.error("Archivo JSON inválido");
      e.target.value = "";
    }
  };

  const submit = async () => {
    if (!file) return;
    if (replace && !window.confirm(`Esto va a ELIMINAR todos los productos actuales y cargar los ${count} del backup. ¿Confirmás?`)) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/backup/products?replace=${replace ? "true" : "false"}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.created} producto(s) restaurado(s)${data.replaced ? " (reemplazados)" : ""}`);
      onImported();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al restaurar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="json-import-modal">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h2 className="font-heading font-bold text-xl flex items-center gap-2"><Upload className="w-5 h-5 text-blue-600" /> Restaurar backup</h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          {!file ? (
            <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-2xl py-10 cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors" data-testid="json-dropzone">
              <Upload className="w-10 h-10 text-blue-500" />
              <span className="text-sm font-semibold text-slate-700">Subí tu archivo de backup .json</span>
              <span className="text-xs text-slate-400">El archivo descargado con "Exportar"</span>
              <input type="file" accept="application/json,.json" className="hidden" onChange={onPick} data-testid="json-file-input" />
            </label>
          ) : (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
              <div className="text-sm font-semibold text-slate-900">{file.name}</div>
              <div className="text-xs text-slate-600 mt-1">Se van a importar <strong>{count}</strong> producto(s).</div>
              <button onClick={() => { setFile(null); setCount(null); }} className="text-xs text-blue-600 hover:underline mt-2">Cambiar archivo</button>
            </div>
          )}

          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} className="mt-0.5" data-testid="json-replace-checkbox" />
            <span><strong className="text-red-600">Reemplazar todo</strong>: borrar los productos actuales antes de importar (recomendado si la base se vació).</span>
          </label>

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
            <button onClick={onClose} className="px-4 py-2 rounded-lg hover:bg-slate-100">Cancelar</button>
            <button onClick={submit} disabled={!file || saving} className="btn-primary" data-testid="json-restore-btn">
              {saving ? "Restaurando..." : `Restaurar ${count || ""}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 6) { toast.error("La nueva contraseña debe tener al menos 6 caracteres"); return; }
    if (form.next !== form.confirm) { toast.error("Las contraseñas no coinciden"); return; }
    if (form.next === form.current) { toast.error("La nueva contraseña debe ser distinta a la actual"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Contraseña actualizada con éxito");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "No se pudo cambiar la contraseña");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="change-password-modal">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h2 className="font-heading font-bold text-xl flex items-center gap-2"><Key className="w-5 h-5 text-blue-600" /> Cambiar contraseña</h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <Field label="Contraseña actual">
            <input required type="password" value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} className="input" data-testid="cp-current" autoFocus />
          </Field>
          <Field label="Nueva contraseña">
            <input required type="password" minLength={6} value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })} className="input" data-testid="cp-new" />
          </Field>
          <Field label="Confirmar nueva contraseña">
            <input required type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="input" data-testid="cp-confirm" />
          </Field>
          <p className="text-xs text-slate-500">Al cambiarla vas a seguir conectado con la sesión actual. Acordate de la nueva clave para tu próximo ingreso.</p>
          <div className="flex justify-end gap-2 pt-3">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg hover:bg-slate-100">Cancelar</button>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="cp-save">{saving ? "Guardando..." : "Cambiar contraseña"}</button>
          </div>
        </form>
        <style>{`.input{width:100%;padding:0.6rem 0.9rem;border:1px solid #e2e8f0;border-radius:0.5rem;outline:none;background:white}.input:focus{border-color:#60a5fa}`}</style>
      </div>
    </div>
  );
}

function CommissionsView({ stats, commissionRate, setCommissionRate }) {
  if (!stats) {
    return <div className="text-center py-12 text-slate-500">Cargando...</div>;
  }
  const rate = Number.isFinite(commissionRate) ? commissionRate : 0;
  const totalCommission = (stats.totals?.revenue || 0) * (rate / 100);

  return (
    <div className="space-y-6" data-testid="commissions-view">
      {/* KPIs */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold"><ShoppingBag className="w-4 h-4" /> Pedidos totales</div>
          <div className="font-heading font-extrabold text-3xl mt-2 text-slate-900" data-testid="kpi-orders">{stats.totals?.orders || 0}</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold"><DollarSign className="w-4 h-4" /> Facturación total</div>
          <div className="font-heading font-extrabold text-3xl mt-2 text-green-700" data-testid="kpi-revenue">{formatARS(stats.totals?.revenue || 0)}</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold"><Sparkles className="w-4 h-4" /> Comisiones a pagar</div>
          <div className="font-heading font-extrabold text-3xl mt-2 text-blue-700" data-testid="kpi-commission">{formatARS(totalCommission)}</div>
          <div className="text-xs text-slate-500 mt-1">al {rate}% sobre facturación</div>
        </div>
      </div>

      {/* Commission rate setting */}
      <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex flex-wrap items-center gap-3">
        <Settings className="w-5 h-5 text-blue-600" />
        <span className="text-sm font-medium text-slate-700">% de comisión por vendedor:</span>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="0"
            max="100"
            step="0.5"
            value={commissionRate}
            onChange={(e) => setCommissionRate(parseFloat(e.target.value) || 0)}
            className="w-24 px-3 py-1.5 rounded-lg border border-slate-200 outline-none focus:border-blue-400 text-right font-semibold"
            data-testid="commission-rate-input"
          />
          <span className="font-semibold">%</span>
        </div>
        <span className="text-xs text-slate-500 ml-auto">Se guarda en este navegador</span>
      </div>

      {/* Sellers table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-heading font-bold text-lg flex items-center gap-2"><Users className="w-5 h-5 text-blue-600" /> Ranking de vendedores</h2>
          <span className="text-xs text-slate-500">{stats.rows.length} vendedor(es) con ventas</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left p-3 w-12">#</th>
              <th className="text-left p-3">Vendedor</th>
              <th className="text-center p-3 hidden sm:table-cell">Cód.</th>
              <th className="text-center p-3">Pedidos</th>
              <th className="text-right p-3">Facturación</th>
              <th className="text-right p-3">Comisión ({rate}%)</th>
              <th className="text-left p-3 hidden md:table-cell w-40">Participación</th>
            </tr>
          </thead>
          <tbody>
            {stats.rows.map((s, i) => {
              const commission = s.total_revenue * (rate / 100);
              const share = stats.totals.revenue > 0 ? (s.total_revenue / stats.totals.revenue) * 100 : 0;
              return (
                <tr key={`${s.code}-${s.name}-${i}`} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`commission-row-${i}`}>
                  <td className="p-3 font-bold text-slate-400">{i + 1}</td>
                  <td className="p-3">
                    <div className="font-medium text-slate-900">{s.name}</div>
                    <div className="text-xs text-slate-500 sm:hidden">Cód. {s.code}</div>
                  </td>
                  <td className="p-3 text-center hidden sm:table-cell"><span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-mono font-bold">{s.code}</span></td>
                  <td className="p-3 text-center font-semibold">{s.orders_count}</td>
                  <td className="p-3 text-right font-bold text-green-700">{formatARS(s.total_revenue)}</td>
                  <td className="p-3 text-right font-semibold text-blue-700">{formatARS(commission)}</td>
                  <td className="p-3 hidden md:table-cell">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-blue-500 to-green-500" style={{ width: `${share}%` }} />
                      </div>
                      <span className="text-xs text-slate-500 w-10 text-right">{share.toFixed(0)}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
            {stats.rows.length === 0 && (
              <tr><td colSpan="7" className="p-10 text-center text-slate-500">Todavía no hay pedidos con vendedor asignado.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Personal seller links */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200">
          <h2 className="font-heading font-bold text-lg flex items-center gap-2"><Link2 className="w-5 h-5 text-blue-600" /> Links personales de vendedores</h2>
          <p className="text-xs text-slate-500 mt-1">Cada vendedor puede compartir su link por WhatsApp. Los clientes que entren se asignan automáticamente.</p>
        </div>
        <div className="divide-y divide-slate-100">
          {SELLERS.map((s) => {
            const link = `${BACKEND_URL}/?vendedor=${s.code}`;
            return (
              <div key={s.code} className="px-5 py-3 flex flex-wrap items-center gap-3" data-testid={`seller-link-${s.code}`}>
                <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-mono font-bold w-10 text-center">{s.code}</span>
                <span className="font-medium text-slate-900 min-w-[160px]">{s.name}</span>
                <code className="flex-1 text-xs bg-slate-50 px-3 py-2 rounded-lg border border-slate-200 truncate">{link}</code>
                <button
                  onClick={() => { navigator.clipboard.writeText(link); toast.success(`Link de ${s.name} copiado`); }}
                  className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold flex items-center gap-1.5 transition-colors"
                  data-testid={`copy-link-${s.code}`}
                >
                  <Copy className="w-3.5 h-3.5" /> Copiar
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <p className="text-xs text-slate-400 italic">El cálculo de comisiones es informativo y se basa en el % configurado. No se descuenta automáticamente del total del pedido.</p>
    </div>
  );
}
